import * as d3 from "d3";
import React, { useEffect, useState, useRef } from "react";
import { drawRect, drawArc, drawCircle, drawLine, drawCornerArc, createCustomPattern, drawText } from "../utils/drawPitchfunctions";
import { drawPlayerConnections, drawPlayers } from "../utils/drawPassingNetwork";
import { renderHeatmap } from "../utils/drawHeatMap";
import { renderRiskMap } from "../utils/drawRiskMap";
import { renderDefensiveHull } from "../utils/drawDefensiveHull";
import PlayerCard from "./PlayerCard";
import { IPlayer, IPlayerConnection } from "../types/IPassingNetworkData";
import PlayerAttackPassData from "../types/IPlayerAttackPassData";
import { ICustomPatternData } from "../types/ICustomPatternData";
import FilterOptions from "../types/IFilterOptions";
import { drawVoronoi } from "../utils/drawVoronoi";
import { IPlayerForVoronoi } from "../types/IVoronoiData";
import IRiskMapData from "../types/IRiskMapData";
import { IAveragePositionData, IEventsPositionData } from "../types/IDefensiveHullData";

interface PitchProps {
  selectedFilters?: FilterOptions;
  passNetworkPlayers?: IPlayer[];
  avgPositionPlayers?: IPlayerForVoronoi[];
  passNetworkPlayerPasses?: IPlayerConnection[];
  playerAttackPassData?: PlayerAttackPassData[];
  riskMapData?: IRiskMapData[];
  defensiveHullData?: {
    firstHullPlayerCoord: IEventsPositionData[];
    secondHullPlayerCoord: IEventsPositionData[];
    firstHullPlayerInner: IEventsPositionData[];
    secondHullPlayerInner: IEventsPositionData[];
    firstPlayerPosition: IAveragePositionData;
    secondPlayerPosition: IAveragePositionData;
    firstPlayerName: string;
    secondPlayerName: string;
  };
}

const circleRadius = window.innerWidth < 800 ? 12 : 25;

const DrawPitch: React.FC<PitchProps> = (pitchProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<IPlayer | IPlayerForVoronoi | null>(null);
  const [showPlayerCard, setShowPlayerCard] = useState<boolean>(false);
  const [cardPosition, setCardPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const createFootballPitch = () => {
      const container = document.getElementById("football-container")!;
      const pitchWidth: number = container.clientWidth;
      const pitchHeight: number = pitchWidth * 0.625; // Maintain a 5:8 aspect ratio
      const svg: d3.Selection<SVGSVGElement, unknown, any, undefined> = d3
        .select<SVGSVGElement, unknown>(svgRef.current!)
        .attr("width", pitchWidth)
        .attr("height", pitchHeight);
      const scale: number = pitchWidth / 800;
      svg.selectAll("*").remove();
      const PatternData: ICustomPatternData[] = [
        { x: 3.5, y: 0, width: 3, height: 10 },
        { x: 0, y: 3.5, width: 10, height: 3 },
      ];
      createCustomPattern(svg, 80, 80, PatternData);
      svg
        .append("rect")
        .attr("width", pitchWidth)
        .attr("height", pitchHeight)
        .style("fill", "black")
        .style("stroke", "white")
        .style("stroke-width", 1)
        .style("opacity", 0.5);
      svg.append("rect").attr("width", pitchWidth).attr("height", pitchHeight).style("fill", "url(#custom-pattern)");
      const xScale = d3.scaleLinear().domain([0, 105]).range([0, pitchWidth]);
      const yScale = d3.scaleLinear().domain([0, 68]).range([0, pitchHeight]);
      const pitchGroup: d3.Selection<SVGGElement, any, null, undefined> = svg.append<SVGGElement>("g");
      const passingAttackGroup: d3.Selection<SVGGElement, any, null, undefined> = svg.append<SVGGElement>("g");
      if (pitchProps.selectedFilters && pitchProps.selectedFilters.isSelectFinalThird) {
        drawRect(pitchGroup, xScale(69.3), yScale(0), xScale(105) - xScale(69.3), yScale(68) - yScale(0), "none", "#005100", 2, 0.2);
        drawText(pitchGroup, xScale(65), yScale(5), 10, "middle", "#0AFC7F", "FINAL 3RD");
      }
      // Draw Left Penalty Area
      drawRect(
        pitchGroup,
        xScale(0),
        yScale(12.92),
        xScale(16.8) - xScale(0),
        yScale(55.08) - yScale(12.92),
        "white",
        "transparent",
        1,
        0.5
      );
      // Draw Left Penalty Arc
      drawArc(pitchGroup, 105 * scale, pitchHeight / 2, 50 * scale, Math.PI * 0.85, Math.PI - Math.PI * 0.85, "white", "transparent", 0.5);
      // Draw Center Circle
      drawCircle(pitchGroup, pitchWidth / 2, pitchHeight / 2, 60 * scale, "transparent", 0.5, "white", 1);
      // Draw Left 6-yard Box
      drawRect(
        pitchGroup,
        xScale(0),
        yScale(25.16),
        xScale(6.3) - xScale(0),
        yScale(42.84) - yScale(25.16),
        "white",
        "transparent",
        1,
        0.5
      );
      // Draw Right Penalty Area
      drawRect(
        pitchGroup,
        xScale(88.2),
        yScale(12.92),
        xScale(105) - xScale(88.2),
        yScale(55.08) - yScale(12.92),
        "white",
        "transparent",
        1,
        0.5
      );
      //Draw Right 6-yard Box
      drawRect(
        pitchGroup,
        xScale(98.7),
        yScale(25.16),
        xScale(105) - xScale(98.7),
        yScale(42.84) - yScale(25.16),
        "white",
        "transparent",
        1,
        0.5
      );
      // Draw Right Penalty Arc
      drawArc(
        pitchGroup,
        pitchWidth - 105 * scale,
        pitchHeight / 2,
        50 * scale,
        Math.PI * 1.15,
        Math.PI + Math.PI * 0.85,
        "white",
        "transparent",
        0.5
      );
      // Draw Centre Spot
      drawCircle(pitchGroup, xScale(105 / 2), yScale(68 / 2), 5 * scale, "white", 0.5);
      // Draw Left Penalty Spot
      drawCircle(pitchGroup, xScale(10.5), yScale(68 / 2), 5 * scale, "white", 0.5);
      // Draw Right Penalty Spot
      drawCircle(pitchGroup, xScale(94.5), yScale(68 / 2), 5 * scale, "white", 0.5);
      // Draw Center Half
      drawCircle(pitchGroup, xScale(94.5), yScale(68 / 2), 5 * scale, "white", 0.5);
      // Draw Center Line
      drawLine(pitchGroup, pitchWidth / 2, 0, pitchWidth / 2, pitchHeight, "white", 1, undefined, 0.5);
      // Define corner data
      const corners = [
        {
          x: xScale(105),
          y: yScale(68),
          startAngle: 6.3,
          endAngle: 1.5 * Math.PI,
        },
        { x: xScale(0), y: yScale(68), startAngle: 1.6, endAngle: 0 },
        { x: xScale(0), y: yScale(0), startAngle: 1.6, endAngle: Math.PI },
        { x: xScale(105), y: yScale(0), startAngle: 4.7, endAngle: Math.PI },
      ];
      // Draw arcs for all corners
      corners.forEach((corner) => {
        drawCornerArc(pitchGroup, 25 * scale, 25 * scale, corner.startAngle, corner.endAngle, corner.x, corner.y, "#4CAF50", "white", 0.5);
      });
      let shiftPressed = false;
      // Check if Shift key is pressed
      const handleKeyPress = (event: { shiftKey: boolean }) => {
        shiftPressed = event.shiftKey;
      };
      window.addEventListener("keydown", handleKeyPress);
      window.addEventListener("keyup", () => {
        shiftPressed = false;
      });
      //Passing Network
      {
        pitchProps.selectedFilters &&
          pitchProps.passNetworkPlayers &&
          pitchProps.passNetworkPlayerPasses &&
          pitchProps.selectedFilters.isDisplayPassingNetwork &&
          drawPlayerConnections(svg, xScale, yScale, pitchProps.passNetworkPlayers, pitchProps.passNetworkPlayerPasses, tooltipRef);
      }
      {
        pitchProps.selectedFilters &&
          pitchProps.avgPositionPlayers &&
          pitchProps.selectedFilters.isVoronoiOfPlayers &&
          drawVoronoi(svg, xScale, yScale, pitchProps.avgPositionPlayers, pitchWidth, pitchHeight);
      }
      {
        pitchProps.selectedFilters &&
          pitchProps.avgPositionPlayers &&
          pitchProps.selectedFilters.isVoronoiOfPlayers &&
          drawPlayers(svg, pitchProps.avgPositionPlayers, xScale, yScale, handlePlayerClick);
      }
      {
        pitchProps.selectedFilters &&
          pitchProps.passNetworkPlayers &&
          pitchProps.selectedFilters.isDisplayPlayer &&
          drawPlayers(svg, pitchProps.passNetworkPlayers, xScale, yScale, handlePlayerClick);
      }
      // Draw Heatmap
      {
        if (
          pitchProps.selectedFilters &&
          pitchProps.playerAttackPassData &&
          pitchProps.selectedFilters.isShowHeatMap &&
          pitchProps.playerAttackPassData.length > 0
        )
          renderHeatmap(passingAttackGroup, xScale, yScale, pitchProps.playerAttackPassData, tooltipRef);
      }
      // Draw Risk Map
      {
        if (
          pitchProps.selectedFilters &&
          pitchProps.riskMapData &&
          pitchProps.selectedFilters.isShowRiskMap &&
          pitchProps.riskMapData.length > 0
        ) {
          renderRiskMap(passingAttackGroup, xScale, yScale, pitchProps.riskMapData, pitchWidth, pitchHeight);
        }
      }
      // Draw Defensive Action Hull
      {
        if (pitchProps.selectedFilters && pitchProps.defensiveHullData && pitchProps.selectedFilters.isShowDefensiveHull) {
          renderDefensiveHull(
            svg,
            xScale,
            yScale,
            {
              eventsPositions: pitchProps.defensiveHullData.firstHullPlayerCoord,
              innerEventsPositions: pitchProps.defensiveHullData.firstHullPlayerInner,
              averagePosition: pitchProps.defensiveHullData.firstPlayerPosition,
              playerName: pitchProps.defensiveHullData.firstPlayerName,
            },
            "red"
          );
          renderDefensiveHull(
            svg,
            xScale,
            yScale,
            {
              eventsPositions: pitchProps.defensiveHullData.secondHullPlayerCoord,
              innerEventsPositions: pitchProps.defensiveHullData.secondHullPlayerInner,
              averagePosition: pitchProps.defensiveHullData.secondPlayerPosition,
              playerName: pitchProps.defensiveHullData.secondPlayerName,
            },
            "blue"
          );
        }
      }
      // Assuming initial scale is 1
      let currentScale = 1;
      const zoomed = ({ transform }: { transform: d3.ZoomTransform }): void => {
        // Check if the Shift key is pressed
        if (!shiftPressed) {
          return;
        }
        currentScale = transform.k;
        // Calculate the translation based on the current zoom transformation
        const xTranslate = Math.min(0, Math.max(+svg.attr("width") * (1 - currentScale), transform.x));
        const yTranslate = Math.min(0, Math.max(+svg.attr("height") * (1 - currentScale), transform.y));
        pitchGroup.attr("transform", `translate(${xTranslate},${yTranslate}) scale(${currentScale})`);
      };

      svg.call(
        d3
          .zoom<SVGSVGElement, unknown>()
          .scaleExtent([1, 10]) // Adjusted lower limit to 1 to prevent zooming out beyond the original size
          .on("zoom", zoomed)
      );
      // Clean up the event listeners on component unmount
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
        window.removeEventListener("keyup", handleKeyPress);
      };
    };
    createFootballPitch();
    window.addEventListener("resize", createFootballPitch);
    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", createFootballPitch);
    };
  }, [pitchProps]);

  const handlePlayerClick = (event: any, player: IPlayer | IPlayerForVoronoi) => {
    setSelectedPlayer(player);
    const point = d3.pointer(event);
    const [x, y] = point;
    const imageHeight = circleRadius * 2;
    const cardWidth = 275; // Adjust according to your card width
    const cardHeight = 400; // Adjust according to your card height
    const cardX = x - cardWidth / 2;
    const cardY = y - cardHeight / 2 - imageHeight / 2;
    setCardPosition({ x: cardX, y: cardY });
    setShowPlayerCard(true);
  };

  const handleClose = () => {
    setShowPlayerCard(!showPlayerCard);
  };

  return (
    <div id="football-container" style={{ position: "relative" }}>
      <svg ref={svgRef} width="1000" height="600"></svg>
      <div
        id="tooltip"
        style={{
          position: "absolute",
          opacity: 0,
          background: "white",
          padding: "5px",
          border: "1px solid black",
          color: "black",
        }}
        ref={tooltipRef}
      ></div>
      {showPlayerCard && (
        <div
          style={{
            position: "absolute",
            top: cardPosition.y,
            left: cardPosition.x,
          }}
        >
          <PlayerCard player={selectedPlayer} cardClose={handleClose} />
        </div>
      )}
    </div>
  );
};

export default DrawPitch;
